'use client'

import { useAuthorization } from '@entities/authentication'
import { CouponModel } from '@entities/coupon/model/coupon.model'
import { CouponCardForPayment } from '@entities/coupon/ui'
import type { KrPaymentOrder } from '@entities/kr-payment/model'
import { paymentEntityQueries } from '@entities/payment/api/payment.query'
import { sortAvailableCoupons } from '@features/coupon/model'
import { RegisterCouponBottomSheet } from '@features/coupon/ui'
import { couponNonApplicableReasonKey } from '@features/payment/model/coupon-reason'
import { usePaymentFlowRevamp } from '@features/purchase-flow'
import { couponResponseAdapter } from '@server/schemas'
import { ANALYTICS_EVENTS, track } from '@shared/analytics'
import { useTranslation } from '@shared/config/i18n'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { match } from 'ts-pattern'

import {
  BottomSheetContent,
  BottomSheetFooter,
  BottomSheetHeader,
  BottomSheetNestedRoot,
  BottomSheetTrigger,
  Box,
  Button,
  Line24pxForwardIcon,
  Typography,
  VStack,
} from '@podo-app/design-system-temp'

type Props = {
  subscribeId: string
  productId?: string | null
  currency?: string
  purchaseOrderId?: string | null
  orderPaymentMethod?: KrPaymentOrder['paymentMethod']
  handleSelectCoupon: (couponId: string) => void
  setRegisterCouponToastId: (id: string) => void
}

const PaymentCouponSelectBottomSheet = ({
  subscribeId,
  productId,
  currency,
  purchaseOrderId,
  orderPaymentMethod,
  handleSelectCoupon,
  setRegisterCouponToastId,
}: Props) => {
  const t = useTranslation()
  const paymentFlowRevamp = usePaymentFlowRevamp()
  const couponCurrency = paymentFlowRevamp ? currency : undefined
  const { bearerToken } = useAuthorization()
  const [isRegisterCouponBottomSheetOpen, setIsRegisterCouponBottomSheetOpen] = useState(false)

  useEffect(() => {
    track(ANALYTICS_EVENTS.POPUP_VIEWED, { name: 'coupon_select', type: 'bottom_sheet' })
  }, [])

  const { data: coupons } = useSuspenseQuery({
    ...paymentEntityQueries.getCouponForPayment({
      bearerToken,
      subscribeId,
      productId,
      purchaseOrderId,
      paymentMethod: orderPaymentMethod,
    }),
  })

  const sortedCoupons = sortAvailableCoupons(coupons.map(couponResponseAdapter))

  const availableCoupons = sortedCoupons.filter((coupon) => coupon.applicable)
  const unavailableCoupons = sortedCoupons.filter((coupon) => !coupon.applicable)

  return (
    <BottomSheetContent>
      <VStack className="h-full justify-between">
        <BottomSheetHeader className="pb-[70px]">
          <Typography size="h1">{t('payment.coupon.selectTitle')}</Typography>

          <VStack className="gap-2">
            <BottomSheetNestedRoot
              open={isRegisterCouponBottomSheetOpen}
              onOpenChange={setIsRegisterCouponBottomSheetOpen}
            >
              <Box
                as={BottomSheetTrigger}
                className="px-3.5 py-4 h-[52px] border rounded-lg border-gray-200 w-full text-start flex items-center justify-between"
              >
                <Typography size="body1">{t('payment.coupon.register')}</Typography>
                <Line24pxForwardIcon className="text-gray-500" />
              </Box>
              <RegisterCouponBottomSheet
                subscribeId={subscribeId}
                productId={productId}
                purchaseOrderId={purchaseOrderId}
                orderPaymentMethod={orderPaymentMethod}
                close={() => setIsRegisterCouponBottomSheetOpen(false)}
                setRegisterCouponSuccessToastId={setRegisterCouponToastId}
              />
            </BottomSheetNestedRoot>

            {[...availableCoupons, ...unavailableCoupons].map((coupon) => {
              const couponModel = CouponModel.create(coupon)
              const raw = coupons.find((candidate) => candidate.coupon_id === coupon.couponId)
              const reasonKey = paymentFlowRevamp ? couponNonApplicableReasonKey(raw?.non_applicable_reason) : null

              return (
                match(coupon)
                  // 사용 가능한 쿠폰
                  .with({ applicable: true }, () => (
                    <BottomSheetTrigger key={coupon.couponId} onClick={() => handleSelectCoupon(coupon.couponId)}>
                      <CouponCardForPayment
                        applicable={true}
                        currency={couponCurrency}
                        title={couponModel.plain.title}
                        discountType={couponModel.discountType}
                        discountAmount={couponModel.plain.discountAmount}
                        discountAmountMax={couponModel.plain.discountAmountMax}
                        isMaximumDiscount={couponModel.isMaximumDiscount}
                        isPercentageDiscount={couponModel.isPercentageDiscount}
                        id={coupon.couponId}
                        useStartedAt={couponModel.plain.utcUseStart}
                        useEndedAt={couponModel.plain.utcUseEnd}
                        isSubscribePayment={couponModel.isSubscribePayment}
                        remainingTimeType={couponModel.remainingTimeType}
                      />
                    </BottomSheetTrigger>
                  ))
                  // 사용 불가능한 쿠폰
                  .with({ applicable: false }, () => (
                    <CouponCardForPayment
                      key={coupon.couponId}
                      applicable={false}
                      currency={couponCurrency}
                      nonApplicableReason={reasonKey ? t(reasonKey) : null}
                      title={couponModel.plain.title}
                      id={coupon.couponId}
                      discountType={couponModel.plain.discountType}
                      discountAmount={couponModel.plain.discountAmount}
                      discountAmountMax={couponModel.plain.discountAmountMax}
                      isMaximumDiscount={couponModel.isMaximumDiscount}
                      isPercentageDiscount={couponModel.isPercentageDiscount}
                      isSubscribePayment={couponModel.isSubscribePayment}
                      useStartedAt={couponModel.plain.utcUseStart}
                      useEndedAt={couponModel.plain.utcUseEnd}
                    />
                  ))
                  .otherwise(() => null)
              )
            })}
          </VStack>
        </BottomSheetHeader>

        <BottomSheetFooter className="absolute bottom-0 left-0 right-0">
          <BottomSheetTrigger asChild>
            <Button size="xLarge" variant="ghost" className="w-full" onClick={() => handleSelectCoupon('')}>
              {t('payment.coupon.useNoCoupon')}
            </Button>
          </BottomSheetTrigger>
        </BottomSheetFooter>
      </VStack>
    </BottomSheetContent>
  )
}

export default PaymentCouponSelectBottomSheet
